/* Quick Add Mini Modal
 * Standalone replacement for the heavy PDP-clone quick-add modal.
 * Fetches /products/{handle}.js on demand and renders only the bare
 * essentials: title, price, options, add-to-cart + view product link.
 */

/* ────────────────────────────────────────────────────────────────────
 * Instant Cart (Kaching) auto-open guard
 *
 * The real structure of Instant Cart's drawer (from the live DOM):
 *
 *   <div id="kaching-cart"
 *        class="kaching-cart ... active"
 *        data-open="true"
 *        data-open-source="add-to-cart">       ← KEY: source of the open
 *     <div class="kaching-cart__overlay">…</div>
 *     <kaching-cart-drawer class="kaching-cart__inner">…</kaching-cart-drawer>
 *   </div>
 *
 * Kaching tags every open with WHY it opened. When an add-to-cart
 * triggers it, they set `data-open-source="add-to-cart"`. When the
 * user clicks the cart icon, the source is different (e.g. "user",
 * "manual", "cart-icon"). We only need to suppress the auto-open case.
 *
 * Implementation:
 *   • MutationObserver on `#kaching-cart` watching class / data-open /
 *     data-open-source. If the source is "add-to-cart", strip every
 *     open marker so the app's CSS reverses the slide-in.
 *   • CSS rule `#kaching-cart[data-open-source="add-to-cart"]`
 *     (see quick-add-mini.css) also hides it visually for the brief
 *     frame between attributes being set and the observer reacting.
 *   • No display:none, no fetch interception, no global flag — purely
 *     scoped to this one source string.
 * ──────────────────────────────────────────────────────────────────── */
(function installKachingGuard() {
  if (window.__qamKachingGuardInstalled) return;
  window.__qamKachingGuardInstalled = true;

  const closeIfAutoOpen = (container) => {
    // Only act while our modal explicitly asked us to suppress. The PDP
    // and any other add-to-cart path is untouched.
    if (!document.body.classList.contains('qam-suppress-cart-drawer')) return;
    if (container.getAttribute('data-open-source') !== 'add-to-cart') return;

    container.classList.remove('active');
    if (container.getAttribute('data-open') === 'true') {
      container.setAttribute('data-open', 'false');
    }
    container.removeAttribute('data-open-source');

    // Try the app's own close button so its internal state stays
    // consistent (it expects its close handler to fire).
    const closeBtn = container.querySelector('.kaching-cart__close');
    if (closeBtn) {
      try { closeBtn.click(); } catch (e) { /* ignore */ }
    }

    // Release any scroll lock the app set on body.
    document.body.classList.remove(
      'kaching-cart-open',
      'kaching-cart-locked',
      'overflow-hidden',
      'no-scroll'
    );
    if (document.body.style.overflow === 'hidden') {
      document.body.style.removeProperty('overflow');
    }
  };

  const guard = (container) => {
    if (!container || container.__qamGuarded) return;
    container.__qamGuarded = true;
    closeIfAutoOpen(container);
    const obs = new MutationObserver(() => closeIfAutoOpen(container));
    obs.observe(container, {
      attributes: true,
      attributeFilter: ['class', 'data-open', 'data-open-source'],
    });
  };

  const sweep = () => {
    const el = document.getElementById('kaching-cart');
    if (el) guard(el);
  };

  const start = () => {
    sweep();
    // The app sometimes injects #kaching-cart after page load.
    const bodyObs = new MutationObserver(sweep);
    bodyObs.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

(function () {
  if (window.customElements.get('quick-add-mini')) return;

  const SIZE_OPTION_NAMES = ['size', 'marime', 'mărime', 'taille', 'größe', 'tamaño', 'tamanho', 'taglia'];
  const COLOR_OPTION_NAMES = ['color', 'colour', 'culoare', 'couleur', 'farbe', 'colore'];

  const strings = () => window.quickAddMiniStrings || {
    addToCart: 'Add to bag',
    soldOut: 'Sold out',
    unavailable: 'Unavailable',
    selectSize: 'Select a Size',
    select: 'Select',
    error: 'There was an error',
  };

  const formatMoney = (cents) => {
    const fmt = window.shopMoneyFormat || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const formatted = fmt
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, value);
    // Strip any HTML the shop's money_format wraps the amount with —
    // common pattern is `<span class="money">£{{amount}}</span>` for
    // currency-converter apps. We display via textContent so otherwise
    // the tags would show as literal text.
    return formatted.replace(/<[^>]*>/g, '').trim();
  };

  const optionRoleFromName = (name) => {
    const n = (name || '').toLowerCase().trim();
    if (COLOR_OPTION_NAMES.includes(n)) return 'color';
    if (SIZE_OPTION_NAMES.includes(n)) return 'size';
    return 'other';
  };

  class QuickAddMini extends HTMLElement {
    constructor() {
      super();
      this.product = null;
      this.selectedOptions = [];
      this.openedBy = null;
      this.cache = new Map();

      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.qam__panel');
      this.loadingEl = this.querySelector('[data-qam-loading]');
      this.contentEl = this.querySelector('[data-qam-content]');
      this.imageEl = this.querySelector('[data-qam-image]');
      this.vendorEl = this.querySelector('[data-qam-vendor]');
      this.titleEl = this.querySelector('[data-qam-title]');
      this.priceEl = this.querySelector('[data-qam-price]');
      this.compareEl = this.querySelector('[data-qam-compare]');
      this.optionsEl = this.querySelector('[data-qam-options]');
      this.formEl = this.querySelector('[data-qam-form]');
      this.variantIdEl = this.querySelector('[data-qam-variant-id]');
      this.addBtn = this.querySelector('[data-qam-add]');
      this.addLabel = this.querySelector('[data-qam-add-label]');
      this.viewEl = this.querySelector('[data-qam-view]');
      this.errorEl = this.querySelector('[data-qam-error]');
      this.successEl = this.querySelector('[data-qam-success]');
      this.linkEls = this.querySelectorAll('[data-qam-link]');
      this.toastEl = document.querySelector('[data-qam-toast]');
      this.toastTextEl = this.toastEl ? this.toastEl.querySelector('[data-qam-toast-text]') : null;
      this.toastTimer = null;
      this.closeTimer = null;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-qam-close]')) {
          e.preventDefault();
          this.hide();
        }
      });

      this.formEl.addEventListener('submit', this.onSubmit);

      // Delegated global trigger
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-add-mini-trigger]');
        if (!trigger) return;
        e.preventDefault();
        const handle = trigger.dataset.productHandle;
        if (!handle) return;
        this.open(handle, trigger);
      });
    }

    setLoading(loading) {
      if (loading) {
        this.loadingEl.hidden = false;
        this.contentEl.hidden = true;
      } else {
        this.loadingEl.hidden = true;
        this.contentEl.hidden = false;
      }
    }

    showError(message) {
      this.errorEl.textContent = message || strings().error;
      this.errorEl.hidden = false;
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    async open(handle, opener) {
      this.openedBy = opener || null;
      this.openerUrl =
        (opener && opener.dataset && opener.dataset.productUrl) || null;
      this.product = null;
      this.selectedOptions = [];
      this.clearError();
      this.hideSuccess();
      this.setLoading(true);
      this.optionsEl.innerHTML = '';
      this.show();

      if (opener) {
        opener.classList.add('is-loading');
      }

      try {
        const product = await this.fetchProduct(handle);
        this.product = product;
        this.render(product);
        this.setLoading(false);
      } catch (err) {
        console.error('[quick-add-mini] fetch failed', err);
        // Kopstorm 09 Aug 2026: never strand the visitor in an empty modal.
        // If we know the product URL, take them to the PDP instead.
        if (this.openerUrl) {
          window.location.href = this.openerUrl;
          return;
        }
        this.setLoading(false);
        this.showError();
      } finally {
        if (opener) opener.classList.remove('is-loading');
      }
    }

    async fetchProduct(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      // Kopstorm 09 Aug 2026: fetch within the active market/locale root.
      // A bare /products/{handle}.js resolves against the default market,
      // which 404s for products scoped to specific markets and returns
      // untranslated titles/options everywhere else.
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}products/${handle}.js`, {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.cache.set(handle, data);
      return data;
    }

    show() {
      this.hidden = false;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('qam-open');
      document.addEventListener('keydown', this.onKeyDown);
      requestAnimationFrame(() => {
        this.panel.focus({ preventScroll: true });
      });
    }

    hide() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      this.hidden = true;
      document.body.classList.remove('qam-open');
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.openedBy && typeof this.openedBy.focus === 'function') {
        this.openedBy.focus({ preventScroll: true });
      }
      // Reset success state so a subsequent open shows the form again.
      this.hideSuccess();
    }

    showSuccess() {
      if (!this.successEl) return;
      this.successEl.hidden = false;
      // Force reflow so the transition triggers reliably.
      // eslint-disable-next-line no-unused-expressions
      this.successEl.offsetHeight;
      this.successEl.classList.add('is-visible');
      this.successEl.setAttribute('aria-hidden', 'false');
    }

    hideSuccess() {
      if (!this.successEl) return;
      this.successEl.classList.remove('is-visible');
      this.successEl.hidden = true;
      this.successEl.setAttribute('aria-hidden', 'true');
    }

    showToast(text) {
      if (!this.toastEl) return;
      if (this.toastTextEl && text) this.toastTextEl.textContent = text;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastEl.hidden = false;
      // eslint-disable-next-line no-unused-expressions
      this.toastEl.offsetHeight;
      this.toastEl.classList.add('is-visible');
      this.toastTimer = setTimeout(() => {
        this.toastEl.classList.remove('is-visible');
        // Hide from a11y tree after fade-out.
        setTimeout(() => {
          if (!this.toastEl.classList.contains('is-visible')) this.toastEl.hidden = true;
        }, 260);
      }, 2400);
    }

    onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
      }
    }

    render(product) {
      this.linkEls.forEach((el) => el.setAttribute('href', product.url || `/products/${product.handle}`));
      this.viewEl.setAttribute('href', product.url || `/products/${product.handle}`);

      const featured = product.featured_image || (product.images && product.images[0]) || '';
      if (featured) {
        const src = typeof featured === 'string' ? featured : featured.src || '';
        this.imageEl.setAttribute('src', this.sizedImage(src, 240));
        this.imageEl.setAttribute('alt', product.title || '');
      } else {
        this.imageEl.removeAttribute('src');
        this.imageEl.setAttribute('alt', '');
      }

      this.vendorEl.textContent = product.vendor || '';
      this.vendorEl.style.display = product.vendor ? '' : 'none';
      this.titleEl.textContent = product.title || '';

      // Initial selection: first available variant, or first variant
      const initial = product.variants.find((v) => v.available) || product.variants[0];
      this.selectedOptions = initial ? initial.options.slice() : product.options.map(() => null);

      this.renderOptions(product);
      this.updateForSelection();
    }

    sizedImage(src, size) {
      if (!src) return '';
      // Shopify image URLs can be resized via _Wx{H} suffix before file extension
      try {
        return src.replace(/(\.(?:jpe?g|png|webp|gif|avif))(\?.*)?$/i, `_${size}x$1$2`);
      } catch (e) {
        return src;
      }
    }

    renderOptions(product) {
      const optionsConfig = product.options.map((rawName, idx) => {
        // product.options can be array of strings OR array of {name, values}
        const name = typeof rawName === 'string' ? rawName : rawName.name;
        const values = typeof rawName === 'string'
          ? Array.from(new Set(product.variants.map((v) => v.options[idx]).filter(Boolean)))
          : rawName.values;
        return { idx, name, values, role: optionRoleFromName(name) };
      });

      // If the only option is the default "Title" with one value, skip rendering.
      if (
        optionsConfig.length === 1 &&
        optionsConfig[0].values.length === 1 &&
        optionsConfig[0].values[0].toLowerCase() === 'default title'
      ) {
        this.optionsEl.innerHTML = '';
        return;
      }

      this.optionsEl.innerHTML = '';
      optionsConfig.forEach((opt) => {
        const block = document.createElement('div');
        block.className = 'qam__option';
        block.dataset.optionIndex = String(opt.idx);
        block.dataset.optionRole = opt.role;

        const label = document.createElement('label');
        label.className = 'qam__option-label';

        if (opt.values.length === 1) {
          // Single value: render as a static label like "Colour: Black"
          label.innerHTML = `<strong>${escapeHtml(opt.name)}:</strong> <span class="qam__option-value" data-qam-selected-value>${escapeHtml(opt.values[0])}</span>`;
          block.appendChild(label);
          this.optionsEl.appendChild(block);
          return;
        }

        // Multi-value: pills
        const isSize = opt.role === 'size';
        const labelText = isSize
          ? `${strings().selectSize}:`
          : `${strings().select} ${opt.name.toLowerCase()}: `;

        label.innerHTML = isSize
          ? escapeHtml(labelText)
          : `${escapeHtml(labelText)}<span class="qam__option-value" data-qam-selected-value></span>`;
        block.appendChild(label);

        const pills = document.createElement('div');
        pills.className = 'qam__pills';

        opt.values.forEach((value) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'qam__pill';
          pill.dataset.value = value;
          pill.dataset.optionIndex = String(opt.idx);
          pill.setAttribute('aria-pressed', 'false');
          pill.textContent = value;
          pill.addEventListener('click', () => {
            if (pill.hasAttribute('data-soldout')) return;
            this.selectOption(opt.idx, value);
          });
          pills.appendChild(pill);
        });

        block.appendChild(pills);
        this.optionsEl.appendChild(block);
      });
    }

    selectOption(optionIdx, value) {
      this.selectedOptions[optionIdx] = value;
      this.clearError();
      this.updateForSelection();
    }

    updateForSelection() {
      if (!this.product) return;

      const matchedVariant = this.findVariant(this.selectedOptions);

      // Update pill states and disabled (sold-out) styling.
      const optionBlocks = this.optionsEl.querySelectorAll('.qam__option');
      optionBlocks.forEach((block) => {
        const optionIdx = Number(block.dataset.optionIndex);

        // Static value display for single-value options keeps in sync.
        const selectedSpan = block.querySelector('[data-qam-selected-value]');
        if (selectedSpan && block.querySelector('.qam__pills')) {
          selectedSpan.textContent = this.selectedOptions[optionIdx] || '';
        }

        const pills = block.querySelectorAll('.qam__pill');
        pills.forEach((pill) => {
          const value = pill.dataset.value;
          const selected = this.selectedOptions[optionIdx] === value;
          pill.setAttribute('aria-pressed', selected ? 'true' : 'false');

          const available = this.isValueAvailable(optionIdx, value);
          if (available) {
            pill.removeAttribute('data-soldout');
            pill.removeAttribute('aria-disabled');
          } else {
            pill.setAttribute('data-soldout', '');
            pill.setAttribute('aria-disabled', 'true');
          }
        });
      });

      // Update price + variant + button state.
      if (matchedVariant) {
        this.variantIdEl.value = matchedVariant.id;
        this.priceEl.textContent = formatMoney(matchedVariant.price);
        if (
          matchedVariant.compare_at_price &&
          matchedVariant.compare_at_price > matchedVariant.price
        ) {
          this.compareEl.textContent = formatMoney(matchedVariant.compare_at_price);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
          this.compareEl.textContent = '';
        }

        // Swap to variant image if it has one.
        if (matchedVariant.featured_image && matchedVariant.featured_image.src) {
          this.imageEl.setAttribute('src', this.sizedImage(matchedVariant.featured_image.src, 240));
        }

        if (matchedVariant.available) {
          this.addBtn.disabled = false;
          this.addLabel.textContent = strings().addToCart;
        } else {
          this.addBtn.disabled = true;
          this.addLabel.textContent = strings().soldOut;
        }
      } else {
        // No variant fully matches yet (user still picking)
        this.variantIdEl.value = '';
        this.addBtn.disabled = true;
        this.addLabel.textContent = this.allOptionsChosen()
          ? strings().unavailable
          : strings().addToCart;

        // Show the base / first available variant price as a hint.
        const fallback =
          this.product.variants.find((v) => v.available) || this.product.variants[0];
        if (fallback) {
          this.priceEl.textContent = formatMoney(fallback.price);
          if (
            fallback.compare_at_price &&
            fallback.compare_at_price > fallback.price
          ) {
            this.compareEl.textContent = formatMoney(fallback.compare_at_price);
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }
      }
    }

    allOptionsChosen() {
      return this.selectedOptions.every((v) => v != null && v !== '');
    }

    findVariant(selected) {
      if (!this.product || !this.product.variants) return null;
      if (!selected.every((v) => v != null && v !== '')) return null;
      return (
        this.product.variants.find((v) =>
          v.options.every((val, i) => val === selected[i])
        ) || null
      );
    }

    /**
     * A value on option `optionIdx` is available if there is at least
     * one variant matching the currently selected options on *other*
     * indexes that uses this value and is in stock.
     */
    isValueAvailable(optionIdx, value) {
      return this.product.variants.some((v) => {
        if (!v.available) return false;
        if (v.options[optionIdx] !== value) return false;
        return v.options.every((val, i) => {
          if (i === optionIdx) return true;
          const sel = this.selectedOptions[i];
          return sel == null || val === sel;
        });
      });
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.addBtn.disabled) return;
      const variantId = this.variantIdEl.value;
      if (!variantId) return;

      this.clearError();
      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;
      const spinner = this.addBtn.querySelector('.qam__cta-spinner');
      if (spinner) spinner.hidden = false;

      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      // Scoped Kaching suppression: only OUR modal's add-to-cart should
      // suppress Instant Cart's drawer. Setting this body class arms
      // both the CSS rule and the MutationObserver above. The PDP and
      // any other add-to-cart path on the site is unaffected.
      document.body.classList.add('qam-suppress-cart-drawer');

      // Safety net: a hard timeout so the button can't get stuck on
      // loading if some upstream code (e.g. a third-party app intercepting
      // /cart/add.js) never resolves. After 12s, abort.
      const fetchAbort = new AbortController();
      const fetchTimeout = setTimeout(() => {
        try { fetchAbort.abort(); } catch (e) { /* ignore */ }
      }, 12000);

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        const sectionIds = this.collectSectionIds(cart);
        if (sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const config =
          typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;
        config.signal = fetchAbort.signal;

        const addUrl =
          (window.routes && window.routes.cart_add_url) || '/cart/add.js';
        const rawRes = await fetch(addUrl, config);
        clearTimeout(fetchTimeout);
        const responseText = await rawRes.text();
        let response;
        try {
          response = JSON.parse(responseText);
        } catch (e) {
          // Some apps return non-JSON (HTML) when they intercept. Treat
          // a non-error HTTP status as success and show the toast anyway.
          response = rawRes.ok ? {} : { status: rawRes.status, description: 'Unexpected response from /cart/add.js' };
        }

        if (response.status) {
          this.showError(response.description || response.message || strings().error);
          return;
        }

        // Update header cart bubble + cart drawer body in place. We don't
        // suppress the cart drawer anymore — any app-provided drawer
        // (e.g. Kaching) is free to open on its own; the modal still
        // shows its own success state + toast for clarity.
        try {
          this.applyCartSections(response, cart);
        } catch (sectionErr) {
          console.warn('[quick-add-mini] section render failed', sectionErr);
        }

        // Notify the rest of the theme that the cart changed (so shipping
        // bars, totals, etc. refresh themselves). `publish`/`PUB_SUB_EVENTS`
        // are top-level globals from constants.js/pubsub.js but `const` is
        // not exposed on `window`, so reach for them by name with a guard.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-mini',
              productVariantId: variantId,
              cartData: response,
            });
          }
        } catch (err) {
          /* ignore */
        }

        // In-modal success → 1s → close → toast.
        const productTitle = (this.product && this.product.title) || '';
        this.showSuccess();
        this.closeTimer = setTimeout(() => {
          this.hide();
          this.showToast(
            productTitle ? `${productTitle} added to cart` : 'Added to cart'
          );
        }, 1000);
      } catch (err) {
        console.error('[quick-add-mini] add failed', err);
        const msg =
          err && err.name === 'AbortError'
            ? 'Request took too long. Please try again.'
            : err && err.message
            ? err.message
            : strings().error;
        this.showError(msg);
      } finally {
        clearTimeout(fetchTimeout);
        this.addBtn.classList.remove('is-loading');
        this.addBtn.disabled = false;
        if (spinner) spinner.hidden = true;
        // Keep the Kaching-suppression class on body long enough for any
        // late-firing auto-open (the app sometimes pops the drawer after
        // its async upsell calc), then lift it so user-initiated cart
        // icon clicks still open the drawer normally afterwards.
        setTimeout(() => {
          document.body.classList.remove('qam-suppress-cart-drawer');
        }, 4000);
      }
    }

    collectSectionIds(cart) {
      const ids = new Set();
      if (cart && typeof cart.getSectionsToRender === 'function') {
        cart.getSectionsToRender().forEach((s) => ids.add(s.id));
      }
      // Always refresh the header bubble in case there is no cart drawer.
      if (document.getElementById('cart-icon-bubble')) ids.add('cart-icon-bubble');
      return Array.from(ids);
    }

    applyCartSections(response, cart) {
      if (!response || !response.sections) return;

      // cart-notification builds a selector from `this.cartItemKey`; mirror
      // what renderContents() would have done so the per-item section
      // selector still resolves.
      if (cart && response.key) {
        cart.cartItemKey = response.key;
      }

      // Re-render any registered cart sections (cart-drawer + cart-icon-bubble),
      // bypassing `cart.renderContents` so the drawer doesn't auto-open.
      if (cart && typeof cart.getSectionsToRender === 'function') {
        cart.getSectionsToRender().forEach((section) => {
          const target = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!target) return;
          const html = response.sections[section.id];
          if (!html) return;
          target.innerHTML = this.extractSectionHTML(html, section.selector);
        });

        // If the drawer was empty before, drop the empty state class so the
        // newly-rendered contents become visible.
        const drawerInner = cart.querySelector && cart.querySelector('.drawer__inner');
        if (drawerInner && drawerInner.classList.contains('is-empty')) {
          drawerInner.classList.remove('is-empty');
        }
        if (cart.classList && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }

        // Re-attach the overlay click-to-close handler that gets wiped when
        // we replaced #CartDrawer's innerHTML.
        if (cart.tagName && cart.tagName.toLowerCase() === 'cart-drawer') {
          const overlay = cart.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof cart.close === 'function') {
            overlay.addEventListener('click', cart.close.bind(cart));
          }
        }
      } else if (response.sections['cart-icon-bubble']) {
        // Fallback: at least refresh the header bubble.
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          bubble.innerHTML = this.extractSectionHTML(response.sections['cart-icon-bubble']);
        }
      }
    }

    extractSectionHTML(html, selector) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const target = doc.querySelector(selector || '.shopify-section') || doc.body;
      return target.innerHTML;
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  customElements.define('quick-add-mini', QuickAddMini);
})();
